import { request, requestBlob } from './core'
import type { AgentProposal, ArchitectureStatus, ProposalPatch, ProposalSuggestion } from './types'

/** A Use Case Hypothesis table row (mirrors `UseCaseRow` in FieldRenderer). */
export interface UseCaseHypothesis {
  name?: string
  description?: string
  type?: 'New' | 'Modernization' | 'Migration'
  impact?: 'H' | 'M' | 'L'
}

/** Snapshot of the AI-proposals loop returned by GET /proposals/ai. */
export interface AIProposalsSnapshot {
  cap: number
  count: number
  remaining_budget: number
  suggestion_status: 'none' | 'running' | 'done' | 'failed'
  suggestion_error?: string | null
  suggestions: ProposalSuggestion[]
  accepted_names: string[]
  skipped_names: string[]
  proposals: AgentProposal[]
  revision: number
  status: string
}

const base = (slug: string) => `/api/assessments/${slug}/proposals`
const POST = { method: 'POST' }

export const proposalsApi = {
  listProposals: (slug: string) =>
    request<{ proposals: AgentProposal[]; complete_count: number }>(base(slug)),

  createProposal: (
    slug: string,
    body: { name: string; description?: string; kind?: string; platform?: string },
    revision?: number,
  ) =>
    request<{ proposal: AgentProposal; revision?: number }>(
      base(slug),
      { method: 'POST', body: JSON.stringify(body) },
      { ifMatch: revision },
    ),

  /** AI-derive candidate use-case rows from intake + existing proposals.
   *  Read-only — the consultant merges them into the table and Saves. */
  generateUseCases: (slug: string) =>
    request<{ use_cases: UseCaseHypothesis[] }>(`${base(slug)}/use-cases/generate`, POST),

  /** AI-draft a proposal (incl. a guesstimated ROI) from pasted requirements. */
  draftProposal: (slug: string, text: string) =>
    request<{ proposal: AgentProposal; revision?: number }>(`${base(slug)}/draft`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  /** AI best-guess fill for one wizard step's fields — a partial-proposal patch
   *  for the wizard to apply + save. Read-only on the server (no If-Match). */
  analyzeProposalStep: (slug: string, proposalId: string, step: string) =>
    request<{ patch: Partial<AgentProposal> }>(`${base(slug)}/${proposalId}/analyze`, {
      method: 'POST',
      body: JSON.stringify({ step }),
    }),

  /** Kick the architecture-diagram worker for one proposal (202; 409 while running). */
  generateArchitecture: (slug: string, proposalId: string) =>
    request<{ status: string }>(`${base(slug)}/${proposalId}/architecture`, POST),

  getArchitecture: (slug: string, proposalId: string) =>
    request<ArchitectureStatus>(`${base(slug)}/${proposalId}/architecture`),

  /** The rendered diagram as a blob (bearer token required — a plain <img src>
   *  can't carry it). Callers turn it into an object URL. */
  getArchitecturePng: (slug: string, proposalId: string) =>
    requestBlob(`${base(slug)}/${proposalId}/architecture.png`),

  /** Field-merge one proposal. Nested objects (roi/agentcore/quick/evaluation/
   *  readiness) deep-merge server-side — send only the changed leaves. */
  updateProposal: (slug: string, proposalId: string, patch: ProposalPatch, revision?: number) =>
    request<{ proposal: AgentProposal; complete: boolean; revision?: number }>(
      `${base(slug)}/${proposalId}`,
      { method: 'PUT', body: JSON.stringify(patch) },
      { ifMatch: revision },
    ),

  deleteProposal: (slug: string, proposalId: string, revision?: number) =>
    request<{ ok: boolean; remaining: number; revision?: number }>(
      `${base(slug)}/${proposalId}`,
      { method: 'DELETE' },
      { ifMatch: revision },
    ),

  beginProposals: (slug: string, revision?: number) =>
    request<{ status: string; revision?: number }>(`${base(slug)}/begin`, POST, { ifMatch: revision }),

  // --- AI proposals loop ---

  beginAIProposals: (slug: string, revision?: number) =>
    request<{ status: string; revision?: number; cap: number }>(`${base(slug)}/ai/begin`, POST, {
      ifMatch: revision,
    }),

  suggestAIProposalsBatch: (slug: string, revision?: number) =>
    request<{ status: string; revision?: number; remaining_budget?: number }>(
      `${base(slug)}/ai/suggest`,
      POST,
      { ifMatch: revision },
    ),

  getAIProposals: (slug: string) => request<AIProposalsSnapshot>(`${base(slug)}/ai`),

  acceptSuggestion: (slug: string, index: number, revision?: number) =>
    request<{ ok: boolean; proposal: AgentProposal; revision?: number }>(
      `${base(slug)}/suggestions/${index}/accept`,
      POST,
      { ifMatch: revision },
    ),

  rejectSuggestion: (slug: string, index: number, revision?: number) =>
    request<{ ok: boolean; revision?: number }>(`${base(slug)}/suggestions/${index}/reject`, POST, {
      ifMatch: revision,
    }),
}
